import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  FileSpreadsheet, 
  Upload, 
  CheckCircle, 
  XCircle,
  Clock,
  AlertTriangle,
  Download,
  Play,
  Trash2
} from 'lucide-react';
import { PageContainer } from '../../components/ui/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { api } from '../../lib/api';

interface BatchTransfer {
  id: string;
  fileName: string;
  totalItems: number;
  totalAmount: number;
  validItems: number;
  invalidItems: number;
  status: 'UPLOADED' | 'VALIDATING' | 'VALIDATED' | 'EXECUTING' | 'COMPLETED' | 'PARTIAL' | 'FAILED';
  createdAt: string;
  executedAt?: string;
}

interface BatchItem {
  id: string;
  recipientName: string;
  recipientDocument: string;
  recipientPixKey: string;
  amount: number;
  description?: string;
  status: 'PENDING' | 'VALID' | 'INVALID' | 'EXECUTED' | 'FAILED';
  errorMessage?: string;
}

interface BatchDetails {
  batch: BatchTransfer;
  items: BatchItem[];
}

export default function BatchTransfer() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Fetch batches
  const { data: batches, isLoading: loadingBatches } = useQuery({
    queryKey: ['batch-transfers'],
    queryFn: async () => {
      const response = await api.get('/banking/batch');
      return response.data as BatchTransfer[];
    },
  });

  // Fetch batch details
  const { data: batchDetails, isLoading: loadingDetails } = useQuery({
    queryKey: ['batch-details', selectedBatch],
    queryFn: async () => {
      const response = await api.get(`/banking/batch/${selectedBatch}/details`);
      return response.data as BatchDetails;
    },
    enabled: !!selectedBatch && showDetailsModal,
  });

  // Upload batch mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/banking/batch/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-transfers'] });
    },
  });

  // Validate batch mutation
  const validateMutation = useMutation({
    mutationFn: async (batchId: string) => {
      const response = await api.post(`/banking/batch/${batchId}/validate`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-transfers'] });
    },
  });

  // Execute batch mutation
  const executeMutation = useMutation({
    mutationFn: async (batchId: string) => {
      const response = await api.post(`/banking/batch/${batchId}/execute`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-transfers'] });
    },
  });

  // Delete batch mutation
  const deleteMutation = useMutation({
    mutationFn: async (batchId: string) => {
      await api.delete(`/banking/batch/${batchId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-transfers'] });
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadMutation.mutate(file);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
      UPLOADED: 'info',
      VALIDATING: 'warning',
      VALIDATED: 'info',
      EXECUTING: 'warning',
      COMPLETED: 'success',
      PARTIAL: 'warning',
      FAILED: 'error',
    };
    const labels: Record<string, string> = {
      UPLOADED: 'Enviado',
      VALIDATING: 'Validando',
      VALIDATED: 'Validado',
      EXECUTING: 'Executando',
      COMPLETED: 'Concluído',
      PARTIAL: 'Parcial',
      FAILED: 'Falhou',
    };
    return <Badge variant={variants[status] || 'info'}>{labels[status] || status}</Badge>;
  };

  const getItemStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
      PENDING: 'info',
      VALID: 'success',
      INVALID: 'error',
      EXECUTED: 'success',
      FAILED: 'error',
    };
    const labels: Record<string, string> = {
      PENDING: 'Pendente',
      VALID: 'Válido',
      INVALID: 'Inválido',
      EXECUTED: 'Executado',
      FAILED: 'Falhou',
    };
    return <Badge variant={variants[status] || 'info'}>{labels[status] || status}</Badge>;
  };

  return (
    <PageContainer
      title="Transferências em Lote"
      description="Envie múltiplas transferências de uma vez"
      icon={FileSpreadsheet}
    >
      {/* Upload Card */}
      <Card className="mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-surface-100">
                Enviar arquivo de transferências
              </h3>
              <p className="text-sm text-surface-400">
                Formatos aceitos: CSV, XLSX
              </p>
            </div>
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploadMutation.isPending}>
              <Upload className="w-4 h-4 mr-2" />
              {uploadMutation.isPending ? 'Enviando...' : 'Selecionar Arquivo'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Template download */}
          <div className="p-4 bg-surface-800 rounded-lg">
            <h4 className="text-sm font-medium text-surface-100 mb-2">Formato do arquivo:</h4>
            <p className="text-sm text-surface-400 mb-3">
              O arquivo deve conter as colunas: nome, documento, chave_pix, valor, descricao (opcional)
            </p>
            <Button variant="ghost" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Baixar modelo
            </Button>
          </div>
        </div>
      </Card>

      {/* Batches List */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-surface-100 mb-4">
            Lotes de Transferência
          </h3>
          
          {loadingBatches ? (
            <div className="text-center py-8 text-surface-400">Carregando...</div>
          ) : batches && batches.length > 0 ? (
            <div className="space-y-4">
              {batches.map((batch) => (
                <div
                  key={batch.id}
                  className="p-4 bg-surface-800 rounded-lg"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                        <FileSpreadsheet className="w-5 h-5 text-primary-400" />
                      </div>
                      <div>
                        <p className="text-surface-100 font-medium">{batch.fileName}</p>
                        <p className="text-sm text-surface-400">{formatDate(batch.createdAt)}</p>
                      </div>
                    </div>
                    {getStatusBadge(batch.status)}
                  </div>

                  <div className="grid grid-cols-4 gap-4 mb-3">
                    <div>
                      <p className="text-xs text-surface-400">Total de itens</p>
                      <p className="text-surface-100 font-medium">{batch.totalItems}</p>
                    </div>
                    <div>
                      <p className="text-xs text-surface-400">Valor total</p>
                      <p className="text-surface-100 font-medium">{formatCurrency(batch.totalAmount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-surface-400">Válidos</p>
                      <p className="text-success font-medium">{batch.validItems}</p>
                    </div>
                    <div>
                      <p className="text-xs text-surface-400">Inválidos</p>
                      <p className="text-error font-medium">{batch.invalidItems}</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedBatch(batch.id);
                        setShowDetailsModal(true);
                      }}
                    >
                      Ver detalhes
                    </Button>
                    
                    {batch.status === 'UPLOADED' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => validateMutation.mutate(batch.id)}
                        disabled={validateMutation.isPending}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Validar
                      </Button>
                    )}
                    
                    {batch.status === 'VALIDATED' && batch.validItems > 0 && (
                      <Button
                        size="sm"
                        onClick={() => executeMutation.mutate(batch.id)}
                        disabled={executeMutation.isPending}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Executar
                      </Button>
                    )}
                    
                    {['UPLOADED', 'VALIDATED'].includes(batch.status) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteMutation.mutate(batch.id)}
                      >
                        <Trash2 className="w-4 h-4 text-error" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-surface-400">
              Nenhum lote encontrado. Envie um arquivo para começar.
            </div>
          )}
        </div>
      </Card>

      {/* Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedBatch(null);
        }}
        title="Detalhes do Lote"
        size="lg"
      >
        {loadingDetails ? (
          <div className="text-center py-8 text-surface-400">Carregando...</div>
        ) : batchDetails ? (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-4 gap-4 p-4 bg-surface-800 rounded-lg">
              <div>
                <p className="text-xs text-surface-400">Total</p>
                <p className="text-surface-100 font-medium">{batchDetails.batch.totalItems}</p>
              </div>
              <div>
                <p className="text-xs text-surface-400">Valor</p>
                <p className="text-surface-100 font-medium">
                  {formatCurrency(batchDetails.batch.totalAmount)}
                </p>
              </div>
              <div>
                <p className="text-xs text-surface-400">Válidos</p>
                <p className="text-success font-medium">{batchDetails.batch.validItems}</p>
              </div>
              <div>
                <p className="text-xs text-surface-400">Inválidos</p>
                <p className="text-error font-medium">{batchDetails.batch.invalidItems}</p>
              </div>
            </div>

            {/* Items list */}
            <div className="max-h-96 overflow-y-auto space-y-2">
              {batchDetails.items.map((item, index) => (
                <div
                  key={item.id}
                  className={`p-3 rounded-lg ${
                    item.status === 'INVALID' || item.status === 'FAILED'
                      ? 'bg-error/10 border border-error/30'
                      : 'bg-surface-800'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-surface-400 text-sm">#{index + 1}</span>
                      <div>
                        <p className="text-surface-100 font-medium">{item.recipientName}</p>
                        <p className="text-sm text-surface-400">{item.recipientPixKey}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-surface-100 font-medium">{formatCurrency(item.amount)}</p>
                      {getItemStatusBadge(item.status)}
                    </div>
                  </div>
                  {item.errorMessage && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-error">
                      <AlertTriangle className="w-4 h-4" />
                      {item.errorMessage}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </Modal>
    </PageContainer>
  );
}
